/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';
import splitLines from 'split-lines';

import {
    type Block,
    type Document,
    type NodeType,
    type Text as SlateText,
    TextRange,
} from '@gitbook/slate';
import type { MarkProps, Plugin } from '@gitbook/slate-react';
import { useIsMounted } from '@gitbook/util-react-hooks';

import { DOMText } from '../../DragAndDrop';
import { useCodeHighlightingStyles } from './styles';
import { resolveCodeBlockSyntax } from './syntaxes';
import { type CodeTokenArgs, tokenizeCode } from './tokenizeCode';
import { useLoadSyntaxes } from './useLoadSyntaxes';

/**
 * For a slate document, load all the syntaxes and return a plugin instance to highlight code blocks.
 */
export function useSlateCodeHighlighter(
    document: Document,
    blockType: string,
    defaultSyntax?: string
): Plugin {
    const codeBlocks = React.useMemo(
        () => document.getBlocksByTypeAsArray(blockType, true),
        [document, blockType]
    );
    // When document is changing, we compute the list of syntaxes
    const syntaxesToLoad = React.useMemo(
        () =>
            codeBlocks
                .reduce<string[]>((syntaxes, block) => {
                    const syntax = getPrismSyntaxForBlock(block, defaultSyntax);
                    if (!syntax || syntaxes.includes(syntax)) {
                        return syntaxes;
                    }
                    syntaxes.push(syntax);
                    return syntaxes;
                }, [])
                .sort(),
        [codeBlocks, defaultSyntax]
    );

    const isMountedRef = useIsMounted();

    const loadedSyntaxes = useLoadSyntaxes(syntaxesToLoad, {
        // During initial rendering, we suspend rendering to load syntaxes
        // to avoid flashing once the syntaxes are loaded
        //
        // When editing (component re-rendering), we don't suspend rendering to avoid flashing
        // the loading spinner when user selects a new syntax for a code-block.
        suspend: !isMountedRef.current,
    });

    return React.useMemo(() => {
        return {
            decorateNode(block: NodeType) {
                if (block.object !== 'block' || block.type !== blockType) {
                    return;
                }

                const prismComponent = getPrismSyntaxForBlock(block, defaultSyntax);
                if (!prismComponent) {
                    return;
                }

                // Split the code block into lines, while still referencing the original text nodes
                // Because of a bug with old documents, some lines may contain newlines.
                const lines = block
                    .getTexts()
                    .toArray()
                    .flatMap((text) => {
                        const lineLines = splitLines(text.text);
                        return lineLines.map((raw) => {
                            return {
                                raw,
                                text,
                            };
                        });
                    });

                return tokenizeCode(lines, prismComponent, {
                    getLineContent(line) {
                        return line.raw;
                    },
                    createToken: createDecoration,
                });
            },
            renderMark,
        };
    }, [loadedSyntaxes, blockType, defaultSyntax]);
}

const TOKEN_MARK = 'prism-token';

/**
 * Render the prism mark
 */
function renderMark(props: MarkProps) {
    const { mark } = props;

    switch (mark.type) {
        case TOKEN_MARK:
            return <CodeHighlightingMark {...props} />;
    }
}

/**
 * Component to render a code highlight mark.
 */
function CodeHighlightingMark(props: MarkProps): React.ReactElement {
    const { mark, children, attributes } = props;
    const classes = mark.data.get('classes') as string;
    const codeHighlightStyles = useCodeHighlightingStyles();

    return (
        <DOMText
            tag="span"
            data-highlighting={classes}
            style={classes.split(' ').map((className) => codeHighlightStyles[className])}
            {...attributes}
        >
            {children}
        </DOMText>
    );
}

/**
 * Return prism syntax for a slate code block.
 */
function getPrismSyntaxForBlock(block: Block, defaultSyntax = 'text'): string | null {
    const grammarName = block.data.get('syntax', defaultSyntax);
    const syntax = resolveCodeBlockSyntax(grammarName);

    if (!syntax.prismComponent) {
        return null;
    }

    return syntax.prismComponent;
}

/**
 * Return a decoration range for the given text.
 */
function createDecoration({
    line,
    lineStart,
    lineEnd,
    start,
    end,
    classes,
}: CodeTokenArgs<{
    raw: string;
    text: SlateText;
}>): TextRange | null {
    if (start >= lineEnd || end <= lineStart) {
        // Ignore, the token is not in the text
        return null;
    }

    if (!classes) {
        // Ignore, the token is not decorated
        return null;
    }

    // Shrink to this text boundaries
    start = Math.max(start, lineStart);
    end = Math.min(end, lineEnd);

    // Now shift offsets to be relative to this text
    start -= lineStart;
    end -= lineStart;

    if (start === end) {
        return null;
    }

    return TextRange.create({
        anchorKey: line.text.key,
        anchorOffset: start,
        focusKey: line.text.key,
        focusOffset: end,
        marks: [{ type: TOKEN_MARK, data: { classes } }],
    });
}
